import React, { useState, useEffect } from 'react';
import { Keyboard, RotateCcw, Edit2, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import { DEFAULT_SHORTCUTS, getShortcuts, saveShortcuts, resetShortcuts, formatShortcut, initializeShortcuts } from '../hooks/useKeyboardShortcuts';

const KeyboardShortcutsSettings = () => {
  const [shortcuts, setShortcuts] = useState(getShortcuts());
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [recordingKeys, setRecordingKeys] = useState(false);
  const [tempShortcut, setTempShortcut] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch shortcuts from server on mount
  useEffect(() => {
    const loadShortcuts = async () => {
      setIsLoading(true);
      try {
        const serverShortcuts = await initializeShortcuts();
        setShortcuts(serverShortcuts);
      } catch (e) {
        console.error('Failed to load shortcuts from server:', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadShortcuts();
  }, []);

  // Group shortcuts by category - filter out any invalid/undefined shortcuts
  const groupedShortcuts = Object.entries(shortcuts).reduce((acc, [shortcutId, shortcut]) => {
    // Skip if shortcut is undefined or not an object
    if (!shortcut || typeof shortcut !== 'object') return acc;
    
    const category = shortcut.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ shortcutId, ...shortcut });
    return acc;
  }, {});

  const handleEditShortcut = (shortcutKey) => {
    setEditingShortcut(shortcutKey);
    setRecordingKeys(true);
    setTempShortcut(null);
    setError(null);
  };

  const handleKeyDown = (event) => {
    if (!recordingKeys) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Ignore modifier-only key presses
    if (['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) return;
    
    const newShortcut = {
      key: event.key,
      ctrl: event.ctrlKey || event.metaKey,
      alt: event.altKey,
      shift: event.shiftKey,
      description: shortcuts[editingShortcut]?.description,
      category: shortcuts[editingShortcut]?.category
    };
    
    // Check for conflicts
    const conflict = Object.entries(shortcuts).find(([shortcutId, s]) => {
      if (shortcutId === editingShortcut) return false;
      return s.key?.toLowerCase() === newShortcut.key?.toLowerCase() &&
             !!s.ctrl === newShortcut.ctrl &&
             !!s.alt === newShortcut.alt &&
             !!s.shift === newShortcut.shift;
    });
    
    if (conflict) {
      const conflictName = conflict[1].description || DEFAULT_SHORTCUTS[conflict[0]]?.description || conflict[0];
      setError(`This shortcut conflicts with "${conflictName}"`);
      setTempShortcut(newShortcut);
    } else {
      setTempShortcut(newShortcut);
      setError(null);
    }
    
    setRecordingKeys(false);
  };

  const handleSaveShortcut = async () => {
    if (!tempShortcut || error) return;
    
    setIsSaving(true);
    
    const newShortcuts = {
      ...shortcuts,
      [editingShortcut]: tempShortcut
    };
    
    setShortcuts(newShortcuts);
    await saveShortcuts(newShortcuts);
    
    setEditingShortcut(null);
    setTempShortcut(null);
    setIsSaving(false);
  };

  const handleCancelEdit = () => {
    setEditingShortcut(null);
    setRecordingKeys(false);
    setTempShortcut(null);
    setError(null);
  };

  const handleResetAll = async () => {
    if (window.confirm('Reset all keyboard shortcuts to defaults?')) {
      setIsSaving(true);
      const defaults = await resetShortcuts();
      setShortcuts(defaults);
      setIsSaving(false);
    }
  };

  const handleResetSingle = async (shortcutKey) => {
    setIsSaving(true);
    const newShortcuts = {
      ...shortcuts,
      [shortcutKey]: DEFAULT_SHORTCUTS[shortcutKey]
    };
    setShortcuts(newShortcuts);
    await saveShortcuts(newShortcuts);
    setIsSaving(false);
  };

  useEffect(() => {
    if (recordingKeys) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [recordingKeys, editingShortcut]);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600 mr-2" />
        <span className="text-gray-600">Loading shortcuts...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Keyboard className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-900">Keyboard Shortcuts</h2>
        </div>
        <button
          onClick={handleResetAll}
          disabled={isSaving}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Reset All</span>
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        Customize keyboard shortcuts to speed up your workflow. Click on a shortcut to change it.
        <span className="block mt-1 text-xs text-green-600">✓ Your shortcuts sync across all your devices.</span>
      </p>

      <div className="space-y-8">
        {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
          <div key={category}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              {category}
            </h3>
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
              {categoryShortcuts.map((shortcut) => (
                <div
                  key={shortcut.shortcutId}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{shortcut.description}</p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {editingShortcut === shortcut.shortcutId ? (
                      <>
                        <div className="flex items-center space-x-2">
                          {recordingKeys ? (
                            <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-sm font-medium animate-pulse">
                              Press keys...
                            </span>
                          ) : tempShortcut ? (
                            <span className={`px-3 py-1.5 rounded text-sm font-medium ${error ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {formatShortcut(tempShortcut)}
                            </span>
                          ) : null}
                          
                          {!recordingKeys && tempShortcut && !error && (
                            <button
                              onClick={handleSaveShortcut}
                              disabled={isSaving}
                              className="p-1.5 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                              title="Save"
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </button>
                          )}
                          
                          <button
                            onClick={handleCancelEdit}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {error && (
                          <div className="flex items-center text-red-600 text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {error}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm font-medium">
                          {formatShortcut(shortcut)}
                        </span>
                        <button
                          onClick={() => handleEditShortcut(shortcut.shortcutId)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit shortcut"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {JSON.stringify(shortcut) !== JSON.stringify({...DEFAULT_SHORTCUTS[shortcut.shortcutId], shortcutId: shortcut.shortcutId}) && (
                          <button
                            onClick={() => handleResetSingle(shortcut.shortcutId)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title="Reset to default"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Tips</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• Use <kbd className="px-1.5 py-0.5 bg-blue-100 rounded text-blue-800">Shift + ?</kbd> anywhere to see available shortcuts</li>
          <li>• Shortcuts won't trigger when typing in text fields (except Escape)</li>
          <li>• Your shortcuts are automatically saved and synced to your account</li>
        </ul>
      </div>
    </div>
  );
};

export default KeyboardShortcutsSettings;
